import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Wallet, CheckCircle2, Clock, PiggyBank } from "lucide-react";

const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

export default function CashToday() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/stats/cash-today")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Errore caricamento"));
  }, []);

  if (err) return null;

  const jobs = data?.jobs || [];
  const total = Number(data?.total || 0);
  const collected = Number(data?.collected || 0);
  const pending = Number(data?.pending || 0);

  return (
    <section
      className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 rounded-2xl border-2 border-emerald-200 p-6 shadow-sm"
      data-testid="cash-today-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold mb-1">
            <PiggyBank className="w-4 h-4" /> Cassa del giorno
          </div>
          <h2 className="font-display text-2xl font-extrabold text-slate-900">
            {EUR.format(total)}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {jobs.length === 0
              ? "Nessun lavoro oggi."
              : "Incassato dai lavori completati oggi"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-full"
            data-testid="cash-collected"
          >
            <CheckCircle2 className="w-3 h-3" /> {EUR.format(collected)}
          </div>
          <div
            className="inline-flex items-center gap-1.5 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full"
            data-testid="cash-pending"
          >
            <Clock className="w-3 h-3" /> da riscuotere {EUR.format(pending)}
          </div>
        </div>
      </div>

      {jobs.length > 0 && (
        <ul className="space-y-2" data-testid="cash-today-list">
          {jobs.map((j) => {
            const isDone = j.status === "completato";
            return (
              <li key={j.id}>
                <Link
                  to={`/lavori/${j.id}`}
                  className="flex items-center gap-3 bg-white border border-slate-200 hover:border-emerald-400 rounded-xl px-3 py-2.5 transition"
                  data-testid={`cash-row-${j.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      isDone ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {j.client_name}
                    </div>
                    <div className="text-[11px] font-mono text-slate-400">
                      {j.start_time}
                      {Number(j.extras_total) > 0 && (
                        <span className="ml-2 text-amber-700">
                          +{EUR.format(j.extras_total)} aggiunte
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 font-display font-extrabold text-lg text-slate-900 tabular-nums">
                    {EUR.format(j.settle_total)}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 pt-3 border-t border-emerald-100 flex items-center justify-between text-xs text-slate-500">
        <div className="inline-flex items-center gap-1.5">
          <Wallet className="w-3.5 h-3.5" />
          <span>La cassa si aggiorna quando il lavoro viene completato · Giallo = ancora da riscuotere</span>
        </div>
      </div>
    </section>
  );
}
